import { signalFeedbackIssue } from '@/composables/useFeedback';
import { getAppEnvironment, getRuntimeConfigValue } from '@/app/runtime';
import * as Sentry from '@sentry/vue';

// Only send trace headers to our own backends (which understand `sentry-trace`),
// never to third parties like the CMS — that would add headers cross-origin for
// no benefit and risk CORS preflight failures. Derived from env so every
// environment (local/acc/prod) scopes itself automatically.
const safeOrigin = (url) => {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
};

const runtimeEnv = (name) => getRuntimeConfigValue(name, process.env[name]);

const envNumber = (name, fallback) => {
  const raw = runtimeEnv(name);
  if (raw === undefined || raw === null || raw === '') return fallback;
  const value = Number(raw);
  return Number.isFinite(value) ? value : fallback;
};

const envFlag = (name, fallback = false) => {
  const raw = runtimeEnv(name);
  if (raw === undefined || raw === null || raw === '') return fallback;
  return ['1', 'true', 'yes', 'on'].includes(String(raw).toLowerCase());
};

const beforeSendLog = (log) => {
  const level = String(log?.level || log?.severity_text || '').toLowerCase();
  return ['error', 'fatal'].includes(level) ? log : null;
};

/**
 * Initialise Sentry for a Vue app: error tracking + performance tracing
 * (router-instrumented) + session replay. No-op when no DSN is configured
 * (local dev, E2E), so it never phones home from developer machines.
 */
export function initSentry({ app, router }) {
  const dsn = runtimeEnv('VUE_APP_SENTRY_DSN_PUBLIC');
  if (!dsn) return;

  const environment = getAppEnvironment(process.env.VUE_APP_ENVIRONMENT || 'production');
  const isProduction = environment === 'production';
  const tracesSampleRate = envNumber('VUE_APP_SENTRY_TRACES_SAMPLE_RATE', isProduction ? 0.01 : 0);

  Sentry.init({
    app,
    dsn,
    environment,
    release: process.env.VUE_APP_COMMIT_HASH || undefined,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration(),
    ],
    // ponytail: error-first defaults; raise trace/replay rates via env only
    // while actively debugging quota-worthy incidents.
    tracesSampleRate,
    tracePropagationTargets: [
      safeOrigin(runtimeEnv('VUE_APP_API_BASE_URL')),
      safeOrigin(runtimeEnv('VUE_APP_SCRUMBLE_API_URL')),
    ].filter(Boolean),
    replaysSessionSampleRate: envNumber('VUE_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE', 0),
    replaysOnErrorSampleRate: envNumber('VUE_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE', 1.0),
    enableLogs: envFlag('VUE_APP_SENTRY_ENABLE_LOGS', false),
    beforeSendLog,
    // Any exception we report is, by definition, a bug worth hearing about —
    // surface the (subtle, dismissible) feedback nudge so the user can tell us
    // what they were doing. Errors only, not messages/transactions.
    beforeSend(event) {
      if (event.exception) {
        const value = event.exception.values?.[0];
        signalFeedbackIssue({
          message: value ? `${value.type}: ${value.value}` : 'error',
          route: typeof window !== 'undefined' ? window.location.hash || window.location.pathname : '',
        });
      }
      return event;
    },
  });
}
